import { AloftPrice, UadListingStatus, expected, isUndefined } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  typeSafeObjectEntries,
  typeSafeObjectFromEntries,
} from '../../../utils/typeSafeObjectEntries.ts';
import { getColorFromAdjustment } from '../adjustmentSelectors/utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from '../adjustmentSelectors/utils/getStringValueFromAdjustment.ts';
import { selectCompSequence } from '../orderSelectors.ts';
import {
  selectManualAdjustmentsById,
  selectManualAdjustmentsEntities,
} from '../propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { selectListingStatus } from '../propertyDataSelectors/transactionSelectors.ts';
import { getShouldApplyDateOfSale } from '../utils/getShouldApplyDateOfSale.ts';
import { getShouldApplyListToSale } from '../utils/getShouldApplyListToSale.ts';
import { calculateMarketAdjustment } from './calculateMarketAdjustment.ts';
import {
  selectAppliedDateOfSaleOrListToSaleAdjustment,
  selectDateOfSaleOrListToSaleAdjustment,
  selectDateOfSaleOrListToSaleAdjustments,
} from './selectDateOfSaleOrListToSaleAdjustment.ts';

// selectCalculatedMarketAdjustment and selectCalculatedMarketAdjustments get
// the market adjustment (terms + concessions + DoS or LtS adj), without
// rounding or thresholds applied. It should be used for any calculations that
// need the raw market adjustment. These are used for the site value
// calculation, market adjusted sale price (which is used to calculate the
// Improvements and Site Value Ratios), and for the tooltip that displays the
// site value calculation string.
export const selectCalculatedMarketAdjustment = createSelector(
  [selectManualAdjustmentsById, selectDateOfSaleOrListToSaleAdjustment],
  (manualAdjustments, dateOfSaleOrListToSaleAdjustment) => {
    if (!manualAdjustments) return AloftPrice.zero;

    return calculateMarketAdjustment({ manualAdjustments, dateOfSaleOrListToSaleAdjustment });
  },
);

// We also need a display version of the raw market adjustment for the analysis tab, cost view.
export const selectCalculatedMarketAdjustmentDisplay = createSelector(
  [selectCalculatedMarketAdjustment],
  (adjustment) => getStringValueFromAdjustment(adjustment),
);

export const selectCalculatedMarketAdjustments = createSelector(
  [selectCompSequence, selectManualAdjustmentsEntities, selectDateOfSaleOrListToSaleAdjustments],
  (compSequence, manualAdjustmentEntities, dateOfSaleOrListToSaleAdjustments) =>
    typeSafeObjectFromEntries(
      ['subject', ...compSequence].map((propID): [string, AloftPrice] => {
        if (propID === 'subject') return [propID, AloftPrice.zero];

        const manualAdjustments = expected(manualAdjustmentEntities[propID]);
        const dateOfSaleOrListToSaleAdjustment =
          dateOfSaleOrListToSaleAdjustments[propID] ?? AloftPrice.zero;

        return [
          propID,
          calculateMarketAdjustment({
            manualAdjustments,
            dateOfSaleOrListToSaleAdjustment,
          }),
        ];
      }),
    ),
);

/**
 * selectAppliedCalculatedMarketAdjustment and
 * selectAppliedCalculatedMarketAdjustments apply rounding and thresholds to the
 * DoS or LtS adjustment before adding it to the terms and concessions. These
 * should be used for display purposes.
 */
export const selectAppliedCalculatedMarketAdjustment = createSelector(
  [selectManualAdjustmentsById, selectAppliedDateOfSaleOrListToSaleAdjustment],
  (manualAdjustments, dateOfSaleOrListToSaleAdjustment) => {
    if (!manualAdjustments) return AloftPrice.zero;

    return calculateMarketAdjustment({ manualAdjustments, dateOfSaleOrListToSaleAdjustment });
  },
);

// Used in Site.tsx
export const selectCalculatedMarketAdjustmentColorDisplay = createSelector(
  selectAppliedCalculatedMarketAdjustment,
  (adjustment) => {
    if (!adjustment) return;

    return {
      value: getStringValueFromAdjustment(adjustment),
      color: getColorFromAdjustment(adjustment),
    };
  },
);

// Used by Site Extraction Commentary - we want the raw values for this
export const selectCalculatedMarketAdjustmentsDisplay = createSelector(
  selectCalculatedMarketAdjustments,
  (adjustments) =>
    typeSafeObjectFromEntries(
      typeSafeObjectEntries(adjustments).map(([propID, adjustment]) => [
        propID,
        getStringValueFromAdjustment(adjustment) ?? '-',
      ]),
    ),
);

const DEFAULT_MARKET_ADJUSTMENTS_LABEL = 'Market';
const getMarketAdjustmentsLabelShortName = (listingStatus?: UadListingStatus) => {
  if (isUndefined(listingStatus)) return DEFAULT_MARKET_ADJUSTMENTS_LABEL;

  const isDateOfSale = getShouldApplyDateOfSale(listingStatus);
  if (isDateOfSale) return 'Date of Sale';

  const isListToSale = getShouldApplyListToSale(listingStatus);
  if (isListToSale) return 'List to Sale';

  return DEFAULT_MARKET_ADJUSTMENTS_LABEL;
};

// Used in Site.tsx for the label
export const selectMarketAdjustmentLabelDisplay = createSelector(
  [selectListingStatus],
  (listingStatus) => {
    const shortName = getMarketAdjustmentsLabelShortName(listingStatus);
    return {
      short: shortName,
      full: `${shortName} Adj`,
    };
  },
);
